import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { COLORS } from "../../constants/colors/colors";
import { CONTRAST_ENUM } from "../../pages/SettingsPage/constants/constants";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "rectangular"
  | "ghost"
  | "round";

interface StyledBaseButtonProps {
  width?: string;
  padding?: string;
}

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "style"
> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  width?: string;
  padding?: string;
  isLoading?: boolean;
  contrast?: CONTRAST_ENUM;
}

const StyledBaseButton = styled.button<StyledBaseButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 2rem;
  font-weight: 700;
  padding: ${(props) => props.padding || "1rem 3rem"};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  gap: 0.5rem;
  width: ${(props) => props.width || "auto"};
  transition: all 0.3s ease;
  white-space: nowrap;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  svg path {
    fill: currentColor;
  }

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-2px)")};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(0)")};
  }
`;

const PrimaryButton = styled(StyledBaseButton)`
  background-color: ${(props) => {
    if (props.disabled) return COLORS.GRAY;
    return COLORS.BOUNDLESS_BLUE;
  }};
  color: ${(props) => {
    if (props.disabled) return COLORS.IMMERSIVE_BLUE_DARK;
    return "#fff";
  }};

  &:hover {
    background-color: ${(props) => {
      if (props.disabled) return COLORS.GRAY;
    }};
    opacity: 0.9;
    color: ${(props) => {
      if (props.disabled) return COLORS.IMMERSIVE_BLUE_DARK;
      return COLORS.WHITE;
    }};
  }
`;

const SecondaryButton = styled(StyledBaseButton)`
  background-color: ${(props) => {
    if (props.disabled) return COLORS.GRAY;
    return COLORS.GRAY;
  }};
  color: ${(props) => {
    if (props.disabled) return COLORS.IMMERSIVE_BLUE_DARK;
    return COLORS.IMMERSIVE_BLUE_DARK;
  }};

  &:hover {
    background-color: ${(props) => {
      if (props.disabled) return COLORS.GRAY;
    }};
    opacity: 0.9;
    color: ${(props) => {
      if (props.disabled) return COLORS.IMMERSIVE_BLUE_DARK;
    }};
  }
`;

const LoadingSpinner = styled.span`
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  display: inline-block;
  animation: button-spin 0.8s linear infinite;

  @keyframes button-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const RectangularButton = styled(StyledBaseButton)<{
  contrast?: CONTRAST_ENUM;
}>`
  width: 100%;
  border-radius: 0.5rem;
  background-color: ${(props) =>
    props.contrast === CONTRAST_ENUM.WHITE
      ? COLORS.BOUNDLESS_BLUE
      : COLORS.WHITE};
  color: ${(props) =>
    props.contrast === CONTRAST_ENUM.WHITE
      ? COLORS.WHITE
      : COLORS.IMMERSIVE_BLUE_DARK};
  font-weight: 700;
  padding: 0.75rem 1rem;

  &:hover {
    background-color: ${(props) =>
      props.contrast === CONTRAST_ENUM.WHITE
        ? COLORS.BOUNDLESS_BLUE
        : "#f5f5f5"};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(1px)")};
    background-color: ${(props) => (props.disabled ? "#f5f5f5" : COLORS.GRAY)};
  }
`;

const GhostButton = styled(StyledBaseButton)<{
  color?: string;
}>`
  background: transparent;
  padding: ${(props) => props.padding || "0"};
  min-width: auto;
  width: auto;
  color: ${(props) => props.color || "unset"};

  &:hover {
    transform: ${(props) => (props.disabled ? "none" : "scale(1.1)")};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "scale(0.95)")};
  }

  &:disabled {
    opacity: 0.4;
  }
`;

const RoundButton = styled(StyledBaseButton)`
  border-radius: 50%;
  padding: ${(props) => props.padding || "1rem"};
  width: ${(props) => props.width || "3rem"};
  height: ${(props) => props.width || "3rem"};
  min-width: ${(props) => props.width || "3rem"};
  min-height: ${(props) => props.width || "3rem"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => {
    if (props.disabled) return COLORS.GRAY;
    return COLORS.BOUNDLESS_BLUE;
  }};
  color: ${(props) => {
    if (props.disabled) return COLORS.IMMERSIVE_BLUE_DARK;
    return COLORS.WHITE;
  }};

  &:hover:not(:disabled) {
    background-color: ${COLORS.BOUNDLESS_BLUE};
    opacity: 0.9;
    color: ${COLORS.WHITE};
  }
  }
`;

const Button = (props: ButtonProps) => {
  const { t } = useTranslation("common");
  const {
    variant = "primary",
    width,
    padding,
    isLoading,
    children,
    contrast,
    color,
    ...rest
  } = props;

  const isDisabled = Boolean(rest.disabled) || Boolean(isLoading);
  const content = isLoading ? (
    <>
      <LoadingSpinner aria-hidden="true" />
      {t("loading")}
    </>
  ) : (
    children
  );

  const sharedProps = {
    ...rest,
    disabled: isDisabled,
    "aria-busy": isLoading || undefined,
    width: width,
    padding: padding,
    children: content,
    color: color,
  };

  switch (variant) {
    case "primary":
      return <PrimaryButton {...sharedProps} />;
    case "secondary":
      return <SecondaryButton {...sharedProps} />;
    case "rectangular":
      return <RectangularButton {...sharedProps} contrast={contrast} />;
    case "ghost":
      return <GhostButton {...sharedProps} />;
    case "round":
      return <RoundButton {...sharedProps} />;
    default:
      return <PrimaryButton {...sharedProps} />;
  }
};

export default Button;
